import React, { useContext } from 'react';
import { Alert, Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/context';
import { useProfileStats } from '../hooks/useProfileStats';
import { ProfileSummaryCard } from '../components/profile/ProfileSummaryCard';
import { ProfileSettingsPanel } from '../components/profile/ProfileSettingsPanel';

const ProfilePage: React.FC = () => {
  const context = useContext(AppContext);
  const { t } = useTranslation('profile');
  const userDetails = context?.userData?.userDetails || { userId: '', email: '', fullName: '' };
  const hasAccess = !!userDetails.userId;
  const { stats, isLoading, error } = useProfileStats(hasAccess ? userDetails.userId : undefined);

  if (!hasAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('page.title')}
          </Typography>
        </Box>
        <Alert severity="info">{t('page.signInRequired')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('page.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {userDetails.fullName || userDetails.email}
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 3 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('page.statsDelayed')}
          </Alert>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('page.statsLoading')}
            </Typography>
          </Box>
        )}
        <ProfileSummaryCard stats={stats} />
        <ProfileSettingsPanel />
      </Paper>
    </Container>
  );
};

export default ProfilePage;
